import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { NUMBER_OF_EVENT_RECORDS_CAN_BE_DOWNLOADED_AT_ONE_TIME } from "../config";
import { TablePageSelector } from "./TablePageSelector";

export const EventRecordsTable = ({
  eventRecords,
  selectedEventRecords,
  setSelectedEventRecords,
  setEventRecordToShowDetails,
  setEventRecordToProcess,
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    // Init select element
    // https://materializecss.com/select.html
    const elems = document.querySelectorAll("select");
    window.M.FormSelect.init(elems);
  }, []);

  const [numOfRowsPerPage, setNumOfRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const numOfPages = Math.ceil(eventRecords.length / numOfRowsPerPage);
  const firstRowIndex = (currentPage - 1) * numOfRowsPerPage;

  useEffect(() => {
    if (currentPage > numOfPages) {
      setCurrentPage(numOfPages || 1);
    }
  }, [currentPage, numOfPages]);

  const handleEventRecordCheckedStateChanged = (recordId, checked) => {
    // console.log('handleEventRecordCheckedStateChanged:', recordId, checked)
    if (checked) {
      if (
        selectedEventRecords.length <
        NUMBER_OF_EVENT_RECORDS_CAN_BE_DOWNLOADED_AT_ONE_TIME
      ) {
        if (!selectedEventRecords.includes(recordId)) {
          setSelectedEventRecords([...selectedEventRecords, recordId]);
        }
      } else {
        alert(
          t("page.kesmdb.downloadErrorToMany", {
            downloadLimit:
              NUMBER_OF_EVENT_RECORDS_CAN_BE_DOWNLOADED_AT_ONE_TIME,
          })
        );
      }
    } else {
      const index_to_remove = selectedEventRecords.indexOf(recordId);
      if (index_to_remove !== -1) {
        const _selectedEventRecordIds = [...selectedEventRecords];
        _selectedEventRecordIds.splice(index_to_remove, 1);
        setSelectedEventRecords(_selectedEventRecordIds);
      }
    }
  };

  const selectAll = () => {
    if (
      selectedEventRecords.length >=
      NUMBER_OF_EVENT_RECORDS_CAN_BE_DOWNLOADED_AT_ONE_TIME
    ) {
      alert(
        t("page.kesmdb.downloadErrorToMany", {
          downloadLimit: NUMBER_OF_EVENT_RECORDS_CAN_BE_DOWNLOADED_AT_ONE_TIME,
        })
      );
    } else {
      const recordIdsInThisPage = eventRecords
        .slice(firstRowIndex, firstRowIndex + numOfRowsPerPage)
        .map(({ id }) => id);
      setSelectedEventRecords(
        [...new Set([...selectedEventRecords, ...recordIdsInThisPage])].slice(
          0,
          NUMBER_OF_EVENT_RECORDS_CAN_BE_DOWNLOADED_AT_ONE_TIME
        )
      );
    }
  };

  const deselectAll = () => {
    const recordIdsToRemove = eventRecords
      .slice(firstRowIndex, firstRowIndex + numOfRowsPerPage)
      .map((record) => record.id);
    setSelectedEventRecords(
      [...selectedEventRecords].filter((recordId) => {
        return recordIdsToRemove.includes(recordId) === false;
      })
    );
  };

  return (
    <>
      <div className="row data_limiter_container">
        <div className="col l10 s6">
          <span className="datalistcount">
            {t("others.tableRowCounter", { rows: eventRecords.length })}
          </span>
        </div>
        <div className="col l1 m2 s4 right">
          <select
            value={numOfRowsPerPage}
            onChange={(e) => setNumOfRowsPerPage(e.target.value)}
          >
            <option value={10}>10개씩</option>
            <option value={20}>20개씩</option>
            <option value={40}>40개씩</option>
            <option value={50}>50개씩</option>
          </select>
        </div>
      </div>
      <div className="table-fixed-container mb20">
        <table
          className="table-fixed table_sort highlight centered datatable2"
          style={{ minWidth: "650px" }}
        >
          <colgroup>
            <col style={{ width: "5%" }} />
            <col style={{ width: "25%" }} />
            <col style={{ width: "27%" }} />
            <col style={{ width: "43%" }} />
          </colgroup>
          <thead>
            <tr className="datatablebg">
              <th>{t("w.select")}</th>
              <th>{t("w.event")}</th>
              <th>{t("w.station")}</th>
              <th>{t("page.kesmdb.eventRecord")}</th>
            </tr>
          </thead>
        </table>
        <div className="clearfix">
          <table
            className="table-bordered table-fixed table_sort highlight centered datatable2"
            style={{ minWidth: "650px" }}
          >
            <colgroup>
              <col style={{ width: "5%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
            </colgroup>
            <thead style={{ overflowWrap: "break-word" }}>
              <tr className="sortable">
                <th className="first nonesort">
                  <label className="datatable_select_radio">
                    <input
                      type="checkbox"
                      name="stationinformation"
                      className="filled-in"
                      onChange={(e) => {
                        if (e.target.checked) {
                          selectAll();
                        } else {
                          deselectAll();
                        }
                      }}
                    />
                    <span></span>
                  </label>
                </th>
                <th className="first">{t("page.kesmdb.originTime")} (KST)</th>
                <th className="first">
                  M<sub>L</sub>
                </th>
                <th className="first">
                  {t("w.code")}
                  <br />({t("w.name")})
                </th>
                <th className="first">
                  V<sub>S30</sub>
                </th>
                <th className="first">
                  H&V<sub>S,Soil</sub>
                </th>
                <th className="first">
                  {t("form.eventRecord.sensorType.title")}
                </th>
                <th className="first">
                  {t("page.kesmdb.epicentralDistance")} (km)
                </th>
                <th className="first">
                  {t("page.kesmdb.horizontalDirection")} PGA (cm/s<sup>2</sup>)
                </th>
                <th className="first">
                  {t("page.kesmdb.verticalDirection")} PGA (cm/s<sup>2</sup>)
                </th>
                <th className="first">{t("page.kesmdb.signalProcessing")}</th>
              </tr>
            </thead>
            <tbody>
              {eventRecords
                .slice(firstRowIndex, firstRowIndex + numOfRowsPerPage)
                .map((record, index) => (
                  <tr
                    key={record.id}
                    style={{ cursor: "pointer" }}
                    onClick={(e) => setEventRecordToShowDetails(record)}
                    title={t("page.kesmdb.waveformPreview")}
                  >
                    <td
                      style={{ cursor: "default" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <label className="datatable_select_radio">
                        <input
                          name="stationinformation"
                          className="filled-in"
                          type="checkbox"
                          checked={selectedEventRecords.includes(record.id)}
                          onChange={(e) =>
                            handleEventRecordCheckedStateChanged(
                              record.id,
                              e.target.checked
                            )
                          }
                        />
                        <span></span>
                      </label>
                    </td>
                    <td
                      className="event-detail-modal-open"
                      data-attr-idx={index}
                    >
                      {record.eventOriginTimeStrKST}
                    </td>
                    <td>
                      {record.evnetMagnitudeML &&
                        record.evnetMagnitudeML.toFixed(1)}
                    </td>
                    <td>
                      {record.stationCode}
                      <br />
                      {`(${record.stationName})`}
                    </td>
                    <td>{record.siteClassVs30}</td>
                    <td>{record.siteClassHVSSoil}</td>
                    <td>
                      {t(`form.eventRecord.sensorType.${record.sensorType}`)}
                    </td>
                    <td>{record.epicentralDistance.toFixed(1)}</td>
                    <td>{Math.max(record.pga.E, record.pga.N).toFixed(6)}</td>
                    <td>{record.pga.Z.toFixed(6)}</td>
                    <td
                      style={{ cursor: "default" }}
                      onClick={(e) => e.stopPropagation()}
                      title=""
                    >
                      <span
                        className="light-blue-text"
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEventRecordToProcess(record);
                        }}
                      >
                        {t("page.kesmdb.open")}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <TablePageSelector
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          numOfPages={numOfPages}
        />
      </div>
    </>
  );
};
